/**
 * FRONTROOMS Retro N64 Texture Mapper
 * Loads tileable pixel-art PNG images from the N64 Texture Pack
 * and configures authentic retro nearest-neighbor filters.
 */

export class TextureGenerator {
  constructor(THREE) {
    this.THREE = THREE;
    this.cache = {};
    this.loader = new THREE.TextureLoader();
  }

  /**
   * Loads a retro texture from the N64 pack with nearest filtering and wrapping.
   */
  loadRetroTexture(name, repeatS = 1, repeatT = 1) {
    const cacheKey = `raw_${name}_${repeatS}_${repeatT}`;
    if (this.cache[cacheKey]) return this.cache[cacheKey];

    const url = `/N64 Texture Pack B/Textures/${name}`;
    const texture = this.loader.load(
      url,
      (txt) => {
        // Success
        txt.needsUpdate = true;
      },
      undefined,
      (err) => {
        console.error(`TextureGenerator: Failed to load retro texture: ${url}`, err);
      }
    );

    texture.wrapS = this.THREE.RepeatWrapping;
    texture.wrapT = this.THREE.RepeatWrapping;
    texture.repeat.set(repeatS, repeatT);

    // CRITICAL RETRO DESIGN: Sharp nearest-neighbor pixelated rendering
    texture.minFilter = this.THREE.NearestFilter;
    texture.magFilter = this.THREE.NearestFilter;
    texture.colorSpace = this.THREE.SRGBColorSpace;

    this.cache[cacheKey] = texture;
    return texture;
  }

  /**
   * Helper to create a basic 2D Canvas and return context + canvas element.
   * Kept for signature compatibility and signs drawing.
   */
  createCanvas(width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    return { canvas, ctx };
  }

  /**
   * Helper to wrap canvas in Three.js CanvasTexture.
   * Kept for signature compatibility.
   */
  wrapTexture(canvas, repeatS = 1, repeatT = 1) {
    const texture = new this.THREE.CanvasTexture(canvas);
    texture.wrapS = this.THREE.RepeatWrapping;
    texture.wrapT = this.THREE.RepeatWrapping;
    texture.repeat.set(repeatS, repeatT);
    texture.colorSpace = this.THREE.SRGBColorSpace;
    texture.needsUpdate = true;
    return texture;
  }

  /**
   * 1. Carpet Texture
   * Maps 'Sand 2.png' (noise sand) tinted beige/tan or vintage red.
   */
  getCarpetMaterial(colorHex = '#7d6b58', bumpScale = 0.015) {
    const cacheKey = `carpet_${colorHex}`;
    if (this.cache[cacheKey]) return this.cache[cacheKey];

    // Sand 2 provides the perfect pixelated carpet grain
    const map = this.loadRetroTexture('Sand 2.png', 35, 35);

    const material = new this.THREE.MeshStandardMaterial({
      map: map,
      color: new this.THREE.Color(colorHex), // applies thematic tint
      roughness: 0.95,
      metalness: 0.0
    });

    this.cache[cacheKey] = material;
    return material;
  }

  /**
   * 2. Ceiling Tile Material
   * Maps 'Stone slabs.png' tinted off-white.
   */
  getCeilingMaterial() {
    const cacheKey = 'ceiling';
    if (this.cache[cacheKey]) return this.cache[cacheKey];

    // Stone slabs provides a beautiful tiled office ceiling grid
    const map = this.loadRetroTexture('Stone slabs.png', 20, 20);

    const material = new this.THREE.MeshStandardMaterial({
      map: map,
      color: new this.THREE.Color('#eae7dd'),
      roughness: 0.85,
      metalness: 0.05
    });

    this.cache[cacheKey] = material;
    return material;
  }

  /**
   * 3. Glowing Ceiling Light panel
   * Maps 'Crystal 3.png' as bright emissive glowing crystals.
   */
  getCeilingLightMaterial() {
    const cacheKey = 'ceiling_light';
    if (this.cache[cacheKey]) return this.cache[cacheKey];

    // Crystal 3 provides a glowing, technical sci-fi light look
    const map = this.loadRetroTexture('Crystal 3.png', 1, 1);

    const material = new this.THREE.MeshStandardMaterial({
      map: map,
      color: new this.THREE.Color('#ffffff'),
      emissive: new this.THREE.Color('#fff7e6'),
      emissiveMap: map,
      emissiveIntensity: 2.2,
      roughness: 0.15,
      metalness: 0.5
    });

    this.cache[cacheKey] = material;
    return material;
  }

  /**
   * 4. Polished Terrazzo / Marble Tile Material
   * Maps 'Rock 3.png' (polished stone tiles) tinted grey or white.
   */
  getTerrazzoMaterial(baseColorHex = '#dfded9') {
    const cacheKey = `terrazzo_${baseColorHex}`;
    if (this.cache[cacheKey]) return this.cache[cacheKey];

    // Rock 3 provides a perfect mosaic concrete terrazzo slate
    const map = this.loadRetroTexture('Rock 3.png', 20, 20);

    const material = new this.THREE.MeshStandardMaterial({
      map: map,
      color: new this.THREE.Color(baseColorHex),
      roughness: 0.12, // Shiny and reflective
      metalness: 0.08
    });

    this.cache[cacheKey] = material;
    return material;
  }

  /**
   * 5. Corporate Wallpaper Material
   * Maps 'Bricks.png' or 'Bricks 2.png' tinted cream or white.
   */
  getWallpaperMaterial(baseColorHex = '#eae5d7', stripeColorHex = '#ded9c9') {
    const cacheKey = `wallpaper_${baseColorHex}`;
    if (this.cache[cacheKey]) return this.cache[cacheKey];

    // Bricks provides textured retro wallpaper panel blocks
    const map = this.loadRetroTexture('Bricks.png', 30, 5.0);

    const material = new this.THREE.MeshStandardMaterial({
      map: map,
      color: new this.THREE.Color(baseColorHex),
      roughness: 0.9,
      metalness: 0.0
    });

    this.cache[cacheKey] = material;
    return material;
  }

  /**
   * 6. Wood Planks Material
   * Maps 'Wood planks.png' for wood floor borders.
   */
  getWoodPlanksMaterial(colorHex = '#5c4033') {
    const cacheKey = `wood_planks_${colorHex}`;
    if (this.cache[cacheKey]) return this.cache[cacheKey];

    const map = this.loadRetroTexture('Wood planks.png', 12, 12);

    const material = new this.THREE.MeshStandardMaterial({
      map: map,
      color: new this.THREE.Color(colorHex),
      roughness: 0.6,
      metalness: 0.1
    });

    this.cache[cacheKey] = material;
    return material;
  }

  /**
   * 7. Stone Bricks Material
   * Maps 'Stone bricks.png' for tiled floor patterns.
   */
  getStoneBricksMaterial(colorHex = '#808080') {
    const cacheKey = `stone_bricks_${colorHex}`;
    if (this.cache[cacheKey]) return this.cache[cacheKey];

    const map = this.loadRetroTexture('Stone bricks.png', 15, 15);

    const material = new this.THREE.MeshStandardMaterial({
      map: map,
      color: new this.THREE.Color(colorHex),
      roughness: 0.7,
      metalness: 0.1
    });

    this.cache[cacheKey] = material;
    return material;
  }

  /**
   * 8. Brushed Steel / Elevator Door Material
   * Maps 'Stone slabs.png' tinted chrome silver with high metalness.
   */
  getMetalMaterial() {
    const cacheKey = 'brushed_metal';
    if (this.cache[cacheKey]) return this.cache[cacheKey];

    const map = this.loadRetroTexture('Stone slabs.png', 6, 2);

    const material = new this.THREE.MeshStandardMaterial({
      map: map,
      color: new this.THREE.Color('#b0b3b8'), // Sleek metal silver
      roughness: 0.25,
      metalness: 0.92
    });

    this.cache[cacheKey] = material;
    return material;
  }

  /**
   * 7. Glowing Signage Textures
   * Keep procedurally drawn canvas overlays since they contain the text descriptions.
   */
  getSignageMaterial(type = 'exit') {
    const lang = window.currentLanguage || 'en';
    const cacheKey = `sign_${type}_${lang}`;
    if (this.cache[cacheKey]) return this.cache[cacheKey];

    const w = 512;
    const h = 256;
    const { canvas, ctx } = this.createCanvas(w, h);

    let isExit = (type === 'exit');
    let bgColor = isExit ? '#0d2b14' : '#141416';
    let glowColor = isExit ? '#39ff14' : '#ffcf40'; // Green exit, Warm gold directory

    ctx.fillStyle = bgColor;
    ctx.fillRect(0, 0, w, h);

    ctx.strokeStyle = '#222';
    ctx.lineWidth = 12;
    ctx.strokeRect(0, 0, w, h);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const tExit = window.translate ? window.translate('EXIT') : 'EXIT';
    const tLift = window.translate ? window.translate('LIFT') : 'LIFT';
    const tCabin = window.translate ? window.translate('EMPTY CABIN INBOUND') : 'EMPTY CABIN INBOUND';
    const tConfs = window.translate ? window.translate('CONFERENCE HALLS') : 'CONFERENCE HALLS';
    const tLobby = window.translate ? window.translate('LOBBY INFINITO') : 'LOBBY INFINITO';
    const tRestrooms = window.translate ? window.translate('RESTROOMS') : 'RESTROOMS';

    if (isExit) {
      ctx.fillStyle = glowColor;
      ctx.shadowColor = glowColor;
      ctx.shadowBlur = 15;
      ctx.font = 'bold 80px "Orbitron", sans-serif';
      ctx.fillText(`${tExit} ➔`, w/2, h/2);
    } 
    else if (type === 'elevator') {
      ctx.fillStyle = glowColor;
      ctx.shadowColor = glowColor;
      ctx.shadowBlur = 12;
      ctx.font = 'bold 64px "Orbitron", sans-serif';
      ctx.fillText(`▲ ${tLift} ▼`, w/2, h/2 - 20);
      ctx.font = '32px "Inter", sans-serif';
      ctx.fillText(tCabin, w/2, h/2 + 45);
    } 
    else if (type === 'directory') {
      ctx.fillStyle = '#fff';
      ctx.shadowBlur = 0;
      ctx.font = 'bold 36px "Orbitron", sans-serif';
      ctx.fillText(`${tConfs} ➔`, w/2, h/4);
      ctx.fillStyle = glowColor;
      ctx.fillText(`➔ ${tLobby.toUpperCase()}`, w/2, h/2);
      ctx.fillStyle = '#999';
      ctx.fillText(`➔ ${tRestrooms}`, w/2, h * 3/4);
    }

    const map = new this.THREE.CanvasTexture(canvas);
    map.colorSpace = this.THREE.SRGBColorSpace;

    // Emissive mask: isolate the glowing text parts
    const { canvas: emissiveCanvas, ctx: eCtx } = this.createCanvas(w, h);
    eCtx.fillStyle = '#000000';
    eCtx.fillRect(0, 0, w, h);

    if (isExit || type === 'elevator' || type === 'directory') {
      eCtx.textAlign = 'center';
      eCtx.textBaseline = 'middle';
      eCtx.fillStyle = glowColor;
      eCtx.shadowColor = glowColor;
      eCtx.shadowBlur = 20;

      if (isExit) {
        eCtx.font = 'bold 80px "Orbitron", sans-serif';
        eCtx.fillText(`${tExit} ➔`, eCtx.canvas.width/2, eCtx.canvas.height/2);
      } else if (type === 'elevator') {
        eCtx.font = 'bold 64px "Orbitron", sans-serif';
        eCtx.fillText(`▲ ${tLift} ▼`, eCtx.canvas.width/2, eCtx.canvas.height/2 - 20);
        eCtx.font = '32px "Inter", sans-serif';
        eCtx.fillText(tCabin, eCtx.canvas.width/2, eCtx.canvas.height/2 + 45);
      } else if (type === 'directory') {
        eCtx.font = 'bold 36px "Orbitron", sans-serif';
        eCtx.fillText(`${tConfs} ➔`, eCtx.canvas.width/2, eCtx.canvas.height/4);
        eCtx.fillStyle = glowColor;
        eCtx.fillText(`➔ ${tLobby.toUpperCase()}`, eCtx.canvas.width/2, eCtx.canvas.height/2);
      }
    }

    const emissiveMap = new this.THREE.CanvasTexture(emissiveCanvas);

    const material = new this.THREE.MeshStandardMaterial({
      map: map,
      emissive: new this.THREE.Color(glowColor),
      emissiveMap: emissiveMap,
      emissiveIntensity: 2.2,
      roughness: 0.15,
      metalness: 0.3
    });

    this.cache[cacheKey] = material;
    return material;
  }

  /**
   * 9. Glowing Doorway Labels
   * Creates a procedurally drawn text texture with a bright emissive glow.
   */
  getDoorwayLabelMaterial(zoneName, colorHex = '#39ff14') {
    const cacheKey = `door_label_${zoneName}_${colorHex}`;
    if (this.cache[cacheKey]) return this.cache[cacheKey];

    const w = 512;
    const h = 128;
    const { canvas, ctx } = this.createCanvas(w, h);

    // Dark sleek frame background
    ctx.fillStyle = '#0b0c0f';
    ctx.fillRect(0, 0, w, h);

    // Glowing border frame
    ctx.strokeStyle = '#23252b';
    ctx.lineWidth = 8;
    ctx.strokeRect(0, 0, w, h);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = 'bold 34px "Orbitron", sans-serif';

    // Renders glowing text
    ctx.fillStyle = colorHex;
    ctx.shadowColor = colorHex;
    ctx.shadowBlur = 10;
    ctx.fillText(`➔ ${zoneName.toUpperCase()}`, w/2, h/2);

    const map = new this.THREE.CanvasTexture(canvas);
    map.colorSpace = this.THREE.SRGBColorSpace;
    map.minFilter = this.THREE.NearestFilter;
    map.magFilter = this.THREE.NearestFilter;

    // Emissive mask to isolate the glowing text parts
    const { canvas: emissiveCanvas, ctx: eCtx } = this.createCanvas(w, h);
    eCtx.fillStyle = '#000000';
    eCtx.fillRect(0, 0, w, h);

    eCtx.textAlign = 'center';
    eCtx.textBaseline = 'middle';
    eCtx.font = 'bold 34px "Orbitron", sans-serif';
    eCtx.fillStyle = colorHex;
    eCtx.shadowColor = colorHex;
    eCtx.shadowBlur = 15;
    eCtx.fillText(`➔ ${zoneName.toUpperCase()}`, w/2, h/2);

    const emissiveMap = new this.THREE.CanvasTexture(emissiveCanvas);
    emissiveMap.minFilter = this.THREE.NearestFilter;
    emissiveMap.magFilter = this.THREE.NearestFilter;

    const material = new this.THREE.MeshStandardMaterial({
      map: map,
      emissive: new this.THREE.Color(colorHex),
      emissiveMap: emissiveMap,
      emissiveIntensity: 2.2,
      roughness: 0.1,
      metalness: 0.5
    });

    this.cache[cacheKey] = material;
    return material;
  }

  /**
   * 10. Glowing Ceiling Signboard Material
   * Creates a procedurally drawn text texture with a bright emissive glow.
   */
  getCeilingSignpostMaterial(arrowText, roomName, glowColor = '#39ff14') {
    const cacheKey = `signpost_${arrowText}_${roomName}_${glowColor}`;
    if (this.cache[cacheKey]) return this.cache[cacheKey];

    const w = 512;
    const h = 128;
    const { canvas, ctx } = this.createCanvas(w, h);

    // Dark directory background
    ctx.fillStyle = '#0a0b0d';
    ctx.fillRect(0, 0, w, h);

    // Clean glowing border
    ctx.strokeStyle = '#22242a';
    ctx.lineWidth = 8;
    ctx.strokeRect(4, 4, w - 8, h - 8);

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Draw the glowing directory arrow + room title
    ctx.fillStyle = glowColor;
    ctx.shadowColor = glowColor;
    ctx.shadowBlur = 10;
    ctx.font = 'bold 26px "Orbitron", sans-serif';
    ctx.fillText(`${arrowText} ${roomName.toUpperCase()}`, w/2, h/2);

    const map = new this.THREE.CanvasTexture(canvas);
    map.colorSpace = this.THREE.SRGBColorSpace;
    map.minFilter = this.THREE.NearestFilter;
    map.magFilter = this.THREE.NearestFilter;

    // Emissive mask
    const { canvas: emissiveCanvas, ctx: eCtx } = this.createCanvas(w, h);
    eCtx.fillStyle = '#000000';
    eCtx.fillRect(0, 0, w, h);

    eCtx.textAlign = 'center';
    eCtx.textBaseline = 'middle';
    eCtx.font = 'bold 26px "Orbitron", sans-serif';
    eCtx.fillStyle = glowColor;
    eCtx.shadowColor = glowColor;
    eCtx.shadowBlur = 15;
    eCtx.fillText(`${arrowText} ${roomName.toUpperCase()}`, w/2, h/2);

    const emissiveMap = new this.THREE.CanvasTexture(emissiveCanvas);
    emissiveMap.minFilter = this.THREE.NearestFilter;
    emissiveMap.magFilter = this.THREE.NearestFilter;

    const material = new this.THREE.MeshStandardMaterial({
      map: map,
      emissive: new this.THREE.Color(glowColor),
      emissiveMap: emissiveMap,
      emissiveIntensity: 2.2,
      roughness: 0.15,
      metalness: 0.5
    });

    this.cache[cacheKey] = material;
    return material;
  }

  /**
   * Helper to parse hex string into rgb components.
   */
  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 128, g: 128, b: 128 };
  }
}
